/**
 * Safe utility functions to prevent crashes from null/undefined values
 * These functions are designed to work even when browser extensions modify the DOM
 */

/**
 * Ensures value is always an array - use before .map() calls
 * @param {*} value - Value that should be an array
 * @returns {Array} Original array or empty array
 */
export const safeArray = (value) => {
  try {
    return Array.isArray(value) ? value : [];
  } catch (e) {
    return [];
  }
};

/**
 * Safe filter that never throws - returns empty array on any error
 * @param {Array} arr - Array to filter
 * @param {Function} predicate - Filter function
 * @returns {Array} Filtered array or empty array
 */
export const safeFilter = (arr, predicate) => {
  try {
    if (!Array.isArray(arr)) return [];
    return arr.filter((item, index) => {
      try {
        return predicate(item, index);
      } catch (e) {
        console.warn("Filter predicate error:", e);
        return false;
      }
    });
  } catch (e) {
    console.error("safeFilter error:", e);
    return [];
  }
};

/**
 * Safe map that never throws - returns empty array on any error
 * @param {Array} arr - Array to map
 * @param {Function} fn - Map function
 * @returns {Array} Mapped array or empty array
 */
export const safeMap = (arr, fn) => {
  try {
    if (!Array.isArray(arr)) return [];
    return arr.map((item, index) => {
      try {
        return fn(item, index);
      } catch (e) {
        console.warn("Map function error:", e);
        return null;
      }
    }).filter(Boolean);
  } catch (e) {
    console.error("safeMap error:", e);
    return [];
  }
};

/**
 * Safe string includes check - handles null/undefined gracefully
 * @param {string} str - String to search in
 * @param {string} search - String to search for
 * @returns {boolean} True if found, false otherwise
 */
export const safeIncludes = (str, search) => {
  try {
    if (str === null || str === undefined) return false;
    if (typeof str !== 'string') str = String(str);
    if (typeof search !== 'string') search = String(search);
    return str.toLowerCase().includes(search.toLowerCase());
  } catch (e) {
    return false;
  }
};

/**
 * Safe array length - returns 0 if not an array
 * @param {Array} arr - Array to check
 * @returns {number} Length or 0
 */
export const safeLength = (arr) => {
  try {
    return Array.isArray(arr) ? arr.length : 0;
  } catch (e) {
    return 0;
  }
};

/**
 * Safe object access - returns default value if path doesn't exist
 * @param {Object} obj - Object to access
 * @param {string} path - Dot-separated path (e.g., "user.name")
 * @param {*} defaultValue - Default value if not found
 * @returns {*} Value at path or default
 */
export const safeGet = (obj, path, defaultValue = null) => {
  try {
    if (!obj || !path) return defaultValue;
    const keys = path.split('.');
    let result = obj;
    for (const key of keys) {
      if (result === null || result === undefined) return defaultValue;
      result = result[key];
    }
    return result !== undefined ? result : defaultValue;
  } catch (e) {
    return defaultValue;
  }
};

/**
 * Safe find - returns null if not found or on error
 * @param {Array} arr - Array to search
 * @param {Function} predicate - Find predicate
 * @returns {*} Found item or null
 */
export const safeFind = (arr, predicate) => {
  try {
    if (!Array.isArray(arr)) return null;
    return arr.find(predicate) || null;
  } catch (e) {
    console.error("safeFind error:", e);
    return null;
  }
};

/**
 * Safe reduce - returns initial value on error
 * @param {Array} arr - Array to reduce
 * @param {Function} fn - Reducer function
 * @param {*} initial - Initial value
 * @returns {*} Reduced value or initial
 */
export const safeReduce = (arr, fn, initial) => {
  try {
    if (!Array.isArray(arr)) return initial;
    return arr.reduce(fn, initial);
  } catch (e) {
    console.error("safeReduce error:", e);
    return initial;
  }
};

/**
 * Safe sort - returns original array on error
 * @param {Array} arr - Array to sort
 * @param {Function} compareFn - Compare function
 * @returns {Array} Sorted array or original
 */
export const safeSort = (arr, compareFn) => {
  try {
    if (!Array.isArray(arr)) return [];
    return [...arr].sort(compareFn);
  } catch (e) {
    console.error("safeSort error:", e);
    return arr || [];
  }
};


/**
 * Safe date formatting - returns fallback on invalid date
 * @param {*} date - Date value to format
 * @param {string} fallback - Fallback string if date is invalid
 * @returns {string} Formatted date or fallback
 */
export const safeFormatDate = (date, fallback = "N/A") => {
  try {
    if (!date) return fallback;
    const d = new Date(date);
    if (isNaN(d.getTime())) return fallback;
    return d.toLocaleDateString("es-MX");
  } catch (e) {
    return fallback;
  }
};

/**
 * Safe number formatting - returns fallback on invalid number
 * @param {*} num - Number to format
 * @param {string} fallback - Fallback string if number is invalid
 * @returns {string} Formatted number or fallback
 */
export const safeFormatNumber = (num, fallback = "0") => {
  try {
    if (num === null || num === undefined) return fallback;
    const n = Number(num);
    if (isNaN(n)) return fallback;
    return n.toLocaleString("es-MX");
  } catch (e) {
    return fallback;
  }
};

/**
 * Safe currency formatting - returns fallback on invalid amount
 * @param {*} amount - Amount to format
 * @param {string} fallback - Fallback string if amount is invalid
 * @returns {string} Formatted currency or fallback
 */
export const safeFormatCurrency = (amount, fallback = "$0.00") => {
  try {
    if (amount === null || amount === undefined) return fallback;
    const n = Number(amount);
    if (isNaN(n)) return fallback;
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN"
    }).format(n);
  } catch (e) {
    return fallback;
  }
};

/**
 * Safe string access - returns fallback if null/undefined
 * @param {*} str - String value
 * @param {string} fallback - Fallback value
 * @returns {string} String or fallback
 */
export const safeString = (str, fallback = "") => {
  try {
    if (str === null || str === undefined) return fallback;
    return String(str);
  } catch (e) {
    return fallback;
  }
};

/**
 * Safe JSON parse - returns fallback on parse error
 * @param {string} jsonString - JSON string to parse
 * @param {*} fallback - Fallback value on error
 * @returns {*} Parsed object or fallback
 */
export const safeJsonParse = (jsonString, fallback = null) => {
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    return fallback;
  }
};

/**
 * Safe property access with default - prevents "Cannot read property of undefined"
 * @param {Function} fn - Function that accesses the property
 * @param {*} fallback - Fallback value
 * @returns {*} Property value or fallback
 */
export const safeTry = (fn, fallback = null) => {
  try {
    const result = fn();
    return result !== undefined && result !== null ? result : fallback;
  } catch (e) {
    return fallback;
  }
};
